import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useProductsList } from 'components/ProductsListModal/ProductsListContext';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Divider, If } from 'shared';
import { ButtonIcon, SwitchField, Typography } from 'shared/Houston';
import { selectMediaQuery } from 'store/selectors/mediaQuery';

import Cancel from '@eduzz/houston-icons/Cancel';
import SearchOutline from '@eduzz/houston-icons/SearchOutline';

import ProductsListItem from './ProductsListItem';
import { FilterContainer, ListContainer, SearchField, Switch } from './styles';

const MIN_CHARS_FOR_ENABLE_SEARCH = 2;

const ProductsList = () => {
  const [term, setTerm] = useState<string | undefined>(undefined);
  const [hideInvalidProducts, setHideInvalidProducts] = useState(true);

  const [, setSearchParams] = useSearchParams();

  const productListContext = useProductsList();
  const mediaQuery = useSelector(selectMediaQuery);
  const searchFieldRef = useRef<HTMLInputElement>(null);

  const termSearch = productListContext?.termSearch;

  const handleClickSwitchInvalidProducts = useCallback(() => {
    setHideInvalidProducts(prevState => !prevState);
  }, [setHideInvalidProducts]);

  useEffect(() => {
    const onToggleHideInvalidProducts = productListContext?.onToggleHideInvalidProducts;
    onToggleHideInvalidProducts?.(hideInvalidProducts);
  }, [hideInvalidProducts, productListContext?.onToggleHideInvalidProducts]);

  useEffect(() => {
    setSearchParams(state => {
      if (!termSearch) {
        return state;
      }

      if (!term || term?.length < MIN_CHARS_FOR_ENABLE_SEARCH) {
        state.delete(termSearch);
        return state;
      }

      state.set(termSearch, term);
      return state;
    });
  }, [setSearchParams, term, termSearch]);

  useEffect(() => {
    searchFieldRef.current?.focus();
  }, [productListContext?.isFetchingByTerm]);

  return (
    <>
      {mediaQuery.md && <Divider margin='0 0 0.5rem 0' />}
      <FilterContainer>
        <SearchField
          ref={searchFieldRef}
          name='searchTerm'
          label='Pesquisar produtos'
          placeholder='Buscar produto por nome ou ID'
          value={term}
          maxLength={255}
          loading={productListContext?.isFetchingByTerm}
          startAdornment={<SearchOutline />}
          fullWidth={false}
          onChange={(value: string) => setTerm(value)}
          endAdornment={
            <If condition={!!term}>
              <ButtonIcon onClick={() => setTerm(undefined)}>
                <Cancel />
              </ButtonIcon>
            </If>
          }
        />
        <Switch>
          <SwitchField
            name='hide-invalid-products'
            checked={hideInvalidProducts}
            onChange={handleClickSwitchInvalidProducts}
          />
          <label htmlFor='hide-invalid-products'>
            <Typography size='xs'>Ocultar Produtos Inválidos</Typography>
          </label>
        </Switch>
      </FilterContainer>
      {mediaQuery.md && <Divider margin='0' />}
      <ListContainer>
        <ProductsListItem />
      </ListContainer>
      {mediaQuery.md && <Divider margin='0' />}
    </>
  );
};

export default memo(ProductsList);
